import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOutlookAuth } from '@/hooks/useOutlookAuth';
import { useOutlookCalendar } from '@/hooks/useOutlookCalendar';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

export default function OutlookCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleCallback } = useOutlookAuth();
  const { checkConnection } = useOutlookCalendar();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Eén inwisseling per autorisatiecode. Deze useEffect hangt aan
  // `checkConnection` en `handleCallback`; zolang die bij elke render een
  // nieuwe functie waren, liep hij een tweede keer. De eerste ronde slaagde en
  // ruimde `sessionStorage` op, waarna de tweede ronde struikelde over "Geen
  // authenticatie data gevonden" — vandaar de foutmelding die verscheen terwijl
  // de koppeling gewoon gelukt was. Microsoft accepteert een code sowieso maar
  // één keer, dus twee pogingen kunnen nooit allebei goed gaan.
  const ingewisseld = useRef<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (code && ingewisseld.current === code) return;

    console.log('[OutlookCallback] Received params:', {
      hasCode: !!code,
      hasState: !!state
    });

    if (!code || !state) {
      setError('Ontbrekende authenticatie parameters. Probeer opnieuw.');
      setProcessing(false);
      return;
    }

    ingewisseld.current = code;

    handleCallback(code, state).then(async (success) => {
      if (success) {
        await checkConnection();
        navigate('/crm/agenda', { replace: true });
      } else {
        setError('De koppeling kon niet worden voltooid.');
        setProcessing(false);
      }
    }).catch((err) => {
      console.error('[OutlookCallback] Error:', err);
      setError(err.message || 'Er is een onverwachte fout opgetreden.');
      setProcessing(false);
    });
  }, [searchParams, handleCallback, checkConnection, navigate]);

  if (processing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
          <h2 className="text-xl font-semibold">Outlook koppelen...</h2>
          <p className="text-muted-foreground">
            Even geduld terwijl we je agenda koppelen
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full space-y-6 text-center">
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-destructive">Koppeling mislukt</h2>
            <p className="text-muted-foreground">{error}</p>
          </div>
          
          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/crm/settings?tab=integrations', { replace: true })}
              className="w-full"
            >
              Probeer opnieuw
            </Button>
            <Button 
              onClick={() => navigate('/crm', { replace: true })}
              variant="outline"
              className="w-full"
            >
              Terug naar Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
